import {useCallback, useEffect, useRef, useState} from 'react';

import {getUserAuditLog} from 'src/openapi/admin/apis/UserAuditApi';
import {useAppDispatch} from 'src/store/hooks';

import type {AuditEntry} from 'src/openapi/admin/models/AuditEntry';

export type UseUserAuditLogReturn = {
    entries: AuditEntry[],
    totalCount: number,
    loading: boolean,
    error: string | null,
    page: number,
    pageSize: number,
    searchQuery: string,
    actionType: string | undefined,
    onPageChange: (page: number) => void,
    onPageSizeChange: (size: number) => void,
    onSearchQueryChange: (query: string) => void,
    onActionTypeChange: (actionType: string | undefined) => void,
};

const DEFAULT_PAGE_SIZE = 25;

export function useUserAuditLog(userId: number): UseUserAuditLogReturn {
    const dispatch = useAppDispatch();
    const [entries, setEntries] = useState<AuditEntry[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [searchQuery, setSearchQuery] = useState('');
    const [actionType, setActionType] = useState<string | undefined>(undefined);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await dispatch(
                    getUserAuditLog(
                        {
                            userId,
                            auditLogRequest: {
                                page: page - 1,
                                pageSize,
                                actionType,
                                searchQuery: searchQuery || undefined,
                            },
                        },
                        {signal: controller.signal},
                    ),
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (response.payload?.success) {
                    setEntries(response.payload.data.items);
                    setTotalCount(response.payload.data.totalCount);
                } else {
                    setError('Failed to load audit log. Please refresh and try again.');
                }
            } catch (err) {
                if ((err as {name?: string})?.name === 'AbortError') {
                    return;
                }
                setError('Failed to load audit log. Please refresh and try again.');
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
                if (abortRef.current === controller) {
                    abortRef.current = null;
                }
            }
        })();

        return () => {
            controller.abort();
        };
    }, [dispatch, userId, page, pageSize, actionType, searchQuery]);

    const onPageSizeChange = useCallback((size: number) => {
        setPageSize(size);
        setPage(1);
    }, []);

    const onSearchQueryChange = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const onActionTypeChange = useCallback((type: string | undefined) => {
        setActionType(type);
        setPage(1);
    }, []);

    return {
        entries,
        totalCount,
        loading,
        error,
        page,
        pageSize,
        searchQuery,
        actionType,
        onPageChange: setPage,
        onPageSizeChange,
        onSearchQueryChange,
        onActionTypeChange,
    };
}
